import moment, { Moment } from 'moment-timezone'
import { BookerCalendarEvent, CurrentBook, Member, MemberInfo } from '@chilipiper/api-type-def'
import { timeConverter } from '@chilipiper/meetings-components'
import { DataType, IntercomData } from '../types'

export const getAssigneesNames = (members: Pick<Member, 'name'>[]): string => {
  const assigneesNames = members.map(member => member.name?.split(' ')[0]).filter(Boolean)
  const othersAmount = assigneesNames.slice(2).length
  const othersText = othersAmount > 1 ? ` & ${othersAmount} others` : ' & 1 other'
  return assigneesNames
    .slice(0, 2)
    .join(', ')
    .concat(othersAmount > 0 ? othersText : '')
}

export const convertEventsTime = (
  events: BookerCalendarEvent[],
  timezone: string
): [BookerCalendarEvent[], Record<string, boolean>] => {
  const availableDays: Record<string, boolean> = {}
  const convertedEvents = events.map(e => {
    const start = timeConverter.convertTimezoneToLocal(e.start, timezone)
    const end = timeConverter.convertTimezoneToLocal(e.end, timezone)
    availableDays[moment(e.start).tz(timezone).format('YYYY-MM-DD')] = true
    return { ...e, start: start.valueOf(), end: end.valueOf() }
  })

  return [convertedEvents, availableDays]
}

interface GetAvailabilityDataArgs {
  assigneeId?: string | null
  assigneesInfo?: MemberInfo[]
  bookingId?: string
  bookme?: CurrentBook
  end: Moment
  intercomData?: IntercomData | null
  isRescheduling: boolean
  meetingEnd: Moment
  meetingStart: Moment
  members: MemberInfo[]
  queue?: any
  routeId?: string
  slot: number
  start: Moment
  timezone: string
}

export const getAvailabilityData = ({
  assigneeId,
  queue,
  members,
  bookme,
  timezone,
  routeId,
  bookingId,
  assigneesInfo,
  isRescheduling,
  intercomData,
  slot,
  start,
  end,
  meetingStart,
  meetingEnd,
}: GetAvailabilityDataArgs): DataType => {
  const bookMeeting = bookme?.bookMeeting

  const predefinedAssignee =
    queue?.enableBroadReschedule && isRescheduling ? undefined : bookMeeting?.assigneeId
  const finalAssigneeId = assigneeId ?? predefinedAssignee

  const guests = bookMeeting?.invitees?.filter(i => i.mandatory).map(i => i.email) ?? []

  const assignees =
    finalAssigneeId && queue?.algorithm !== 'Group'
      ? [finalAssigneeId]
      : ((members ?? []).map(a => a.id) as string[])
  const duration = intercomData?.duration ? intercomData.duration : bookMeeting?.duration

  const result = {
    assigneesIds: assignees,
    start: start.valueOf(),
    end: end.valueOf(),
    timezone: intercomData?.timezone ? intercomData.timezone : timezone,
    meetingDuration: duration || 15,
    step: duration !== undefined && (duration <= 15 || duration === 45) ? duration : 30,
    slot,
    withAvailableDays: true,
    withDelay: true,
    withBuffers: true,
    guests: guests,
    template: intercomData?.template ? intercomData.template : (bookMeeting?.template ?? ''),
    assigneesInfo: assigneesInfo || [],
    routeId: routeId,
    bookingId: bookingId || null,
    roomsIds: bookMeeting?.roomId ? [bookMeeting.roomId] : [],
    queueId: undefined,
    meetingStart: meetingStart,
    meetingEnd: meetingEnd,
  }
  if (queue) {
    result.queueId = queue.id
  }
  return result
}
